(function () {
  let pendingBfcacheReset = false;

  window.addEventListener('pageshow', (event) => {
    const navEntry = performance.getEntriesByType('navigation')[0];
    if (event.persisted || navEntry?.type === 'back_forward') {
      pendingBfcacheReset = true;
    }
  });

  function getAccessoriesPicker(productForm) {
    return productForm?.closest('product-info')?.querySelector(
      `xlean-accessories[data-section-id="${productForm.dataset.sectionId}"]`
    );
  }

  window.xleanAccessories = {
    forProductForm(productForm) {
      const picker = getAccessoriesPicker(productForm);
      if (!picker) return null;
      const selected = picker.getSelectedItems();
      if (!selected.length) return null;
      return { items: selected };
    },
  };

  class XleanAccessories extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.sectionId = this.dataset.sectionId;
      this.productInfo = document.getElementById(`MainProduct-${this.sectionId}`);
      this.checkoutLink = document.getElementById(`ProductCheckoutLink-${this.sectionId}`);

      this.bindEvents();
      this.syncCardStates();
    }

    bindEvents() {
      this.querySelectorAll('[data-accessory-card]').forEach((card) => {
        const checkbox = card.querySelector('[data-accessory-checkbox]');
        const variantSelect = card.querySelector('[data-accessory-variant-select]');

        card.addEventListener('click', (event) => this.onCardClick(card, event));
        card.addEventListener('keydown', (event) => this.onCardKeydown(card, event));

        checkbox?.addEventListener('change', () => {
          this.syncCardState(card);
          this.mergeIntoCheckoutLink();
        });

        variantSelect?.addEventListener('click', (event) => event.stopPropagation());
        variantSelect?.addEventListener('change', (event) => {
          event.stopPropagation();
          this.onVariantSelectChange(card, variantSelect);
        });
      });

      this._checkoutLinkUpdatedHandler = () => this.mergeIntoCheckoutLink();
      document.addEventListener('xlean:checkout-link-updated', this._checkoutLinkUpdatedHandler);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this._variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (!data || String(data.sectionId) !== String(this.sectionId)) return;
          if (pendingBfcacheReset) {
            pendingBfcacheReset = false;
            this.resetSelections();
            return;
          }
          window.requestAnimationFrame(() => this.mergeIntoCheckoutLink());
        });
      }
    }

    disconnectedCallback() {
      document.removeEventListener('xlean:checkout-link-updated', this._checkoutLinkUpdatedHandler);
      this._variantChangeUnsubscriber?.();
    }

    onCardClick(card, event) {
      if (card.classList.contains('is-disabled')) return;
      if (event.target.closest('select, [data-accessory-variant-select]')) return;

      const checkbox = card.querySelector('[data-accessory-checkbox]');
      if (!checkbox || checkbox.disabled) return;

      checkbox.checked = !checkbox.checked;
      checkbox.dispatchEvent(new Event('change', { bubbles: true }));
    }

    onCardKeydown(card, event) {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      if (event.target.closest('select')) return;
      event.preventDefault();
      this.onCardClick(card, event);
    }

    resetSelections() {
      this.querySelectorAll('[data-accessory-checkbox]').forEach((checkbox) => {
        checkbox.checked = false;
      });
      this.syncCardStates();
      this.mergeIntoCheckoutLink();
    }

    getSelectedItems() {
      const items = [];
      this.querySelectorAll('[data-accessory-card]').forEach((card) => {
        const checkbox = card.querySelector('[data-accessory-checkbox]');
        if (!checkbox?.checked || checkbox.disabled) return;
        const variantId = this.getCardVariantId(card);
        if (!variantId) return;
        items.push({ id: variantId, quantity: 1 });
      });
      return items;
    }

    getCardVariantId(card) {
      const variantSelect = card.querySelector('[data-accessory-variant-select]');
      return variantSelect?.value || null;
    }

    onVariantSelectChange(card, variantSelect) {
      const selectedOption = variantSelect.selectedOptions?.[0];
      const checkbox = card.querySelector('[data-accessory-checkbox]');
      const priceEl = card.querySelector('[data-accessory-price-value]');
      const isAvailable = selectedOption && !selectedOption.disabled;

      if (checkbox) {
        checkbox.value = variantSelect.value;
        checkbox.disabled = !isAvailable;
        if (!isAvailable) checkbox.checked = false;
      }

      if (priceEl && selectedOption) {
        priceEl.textContent = selectedOption.dataset.priceHtml || '';
        const priceWrap = card.querySelector('[data-accessory-price]');
        const soldOutLabel = priceWrap?.querySelector('.xlean-accessories__sold-out');
        if (!isAvailable && !soldOutLabel) {
          const soldOut = document.createElement('span');
          soldOut.className = 'xlean-accessories__sold-out';
          soldOut.textContent = window.variantStrings?.soldOut || 'Sold out';
          priceWrap?.appendChild(soldOut);
        } else if (isAvailable && soldOutLabel) {
          soldOutLabel.remove();
        }
      }

      card.classList.toggle('is-disabled', !isAvailable);
      if (!isAvailable) {
        card.removeAttribute('tabindex');
        card.removeAttribute('role');
        card.removeAttribute('aria-checked');
      } else {
        card.setAttribute('tabindex', '0');
        card.setAttribute('role', 'checkbox');
      }

      this.syncCardState(card);
      this.mergeIntoCheckoutLink();
    }

    syncCardStates() {
      this.querySelectorAll('[data-accessory-card]').forEach((card) => this.syncCardState(card));
    }

    syncCardState(card) {
      const checkbox = card.querySelector('[data-accessory-checkbox]');
      const visualCheckbox = card.querySelector('[data-accessory-checkbox-visual]');
      const isChecked = Boolean(checkbox?.checked && !checkbox.disabled);

      card.classList.toggle('is-selected', isChecked);
      card.setAttribute('aria-checked', isChecked ? 'true' : 'false');
      if (visualCheckbox) visualCheckbox.checked = isChecked;
    }

    mergeIntoCheckoutLink() {
      if (!this.checkoutLink || this.checkoutLink.getAttribute('aria-disabled') === 'true') return;

      const selected = this.getSelectedItems();
      let href = this.checkoutLink.href;
      if (!href || href === '#') return;

      try {
        const url = new URL(href, window.location.origin);
        const cartMatch = url.pathname.match(/\/cart\/(.+)$/);
        if (!cartMatch) return;

        const baseItem = cartMatch[1].split(',')[0];
        const itemSegments = [baseItem, ...selected.map((item) => `${item.id}:${item.quantity}`)];
        url.pathname = `${window.routes?.cart_url || '/cart'}/${itemSegments.join(',')}`;

        if (!url.search.includes('checkout')) {
          url.search += url.search ? '&checkout' : '?checkout';
        }

        this.checkoutLink.href = `${url.pathname}${url.search}`;
      } catch (error) {
        console.error(error);
      }
    }
  }

  if (!customElements.get('xlean-accessories')) {
    customElements.define('xlean-accessories', XleanAccessories);
  }
})();
